import { createContext, type ReactNode, use } from 'react';
import {
  Pressable,
  type PressableProps,
  type StyleProp,
  View,
  type ViewStyle,
} from 'react-native';
import { StyleSheet } from 'react-native-unistyles';

type RadioVariant =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'info'
  | 'error';
type RadioSize = 'sm' | 'md' | 'lg';
type RadioDirection = 'horizontal' | 'vertical';

type RadioGroupContextValue = {
  disabled?: boolean;
  onValueChange?: (value: string) => void;
  size: RadioSize;
  value?: string;
  variant: RadioVariant;
};

interface RadioGroupProps {
  children: ReactNode;
  direction?: RadioDirection;
  disabled?: boolean;
  onValueChange?: (value: string) => void;
  size?: RadioSize;
  space?: 'xs' | 'sm' | 'md' | 'lg';
  style?: StyleProp<ViewStyle>;
  value?: string;
  variant?: RadioVariant;
}

interface RadioOptionProps
  extends Omit<
    PressableProps,
    | 'accessibilityRole'
    | 'accessibilityState'
    | 'children'
    | 'disabled'
    | 'style'
  > {
  children: ReactNode;
  disabled?: boolean;
  style?: StyleProp<ViewStyle>;
  value: string;
}

const RadioGroupContext = createContext<RadioGroupContextValue | undefined>(
  undefined,
);

function RadioGroupRoot({
  children,
  direction = 'vertical',
  disabled,
  onValueChange,
  size = 'md',
  space = 'sm',
  style: extraStyles,
  value,
  variant = 'primary',
}: RadioGroupProps) {
  groupStyles.useVariants({
    direction,
    space,
  });

  return (
    <RadioGroupContext
      value={{
        disabled,
        onValueChange,
        size,
        value,
        variant,
      }}
    >
      <View style={[groupStyles.root, extraStyles]}>{children}</View>
    </RadioGroupContext>
  );
}

function RadioOption({
  children,
  disabled,
  onPress,
  style: extraStyles,
  value,
  ...props
}: RadioOptionProps) {
  const group = use(RadioGroupContext);
  const checked = group?.value === value;
  const optionDisabled = disabled || group?.disabled;
  const size = group?.size ?? 'md';
  const variant = group?.variant ?? 'primary';

  optionStyles.useVariants({
    checked,
    disabled: optionDisabled,
    size,
    variant,
  });

  const pressOption: PressableProps['onPress'] = (event) => {
    if (optionDisabled) {
      return;
    }

    group?.onValueChange?.(value);
    onPress?.(event);
  };

  return (
    <Pressable
      {...props}
      aria-checked={checked}
      accessibilityRole="radio"
      accessibilityState={{ checked, disabled: optionDisabled }}
      disabled={optionDisabled}
      onPress={pressOption}
      style={[optionStyles.root, extraStyles]}
    >
      <View pointerEvents="none" style={optionStyles.indicator}>
        <View style={optionStyles.dot} />
      </View>
      {children}
    </Pressable>
  );
}

const RadioGroup = Object.assign(RadioGroupRoot, {
  Option: RadioOption,
});

export default RadioGroup;

const groupStyles = StyleSheet.create((theme) => ({
  root: {
    variants: {
      direction: {
        horizontal: {
          flexDirection: 'row',
        },
        vertical: {
          flexDirection: 'column',
        },
      },
      space: {
        lg: {
          gap: theme.space('lg'),
        },
        md: {
          gap: theme.space('md'),
        },
        sm: {
          gap: theme.space('sm'),
        },
        xs: {
          gap: theme.space('xs'),
        },
      },
    },
  },
}));

const optionStyles = StyleSheet.create((theme) => ({
  dot: {
    borderRadius: theme.borderRadius.full,
    opacity: 0,
    variants: {
      checked: {
        true: {
          opacity: 1,
        },
      },
      disabled: {
        true: {},
      },
      size: {
        lg: {
          height: theme.size(3),
          width: theme.size(3),
        },
        md: {
          height: theme.size(2.5),
          width: theme.size(2.5),
        },
        sm: {
          height: theme.size(2),
          width: theme.size(2),
        },
      },
      variant: {
        error: {
          backgroundColor: theme.colors.error,
        },
        info: {
          backgroundColor: theme.colors.info,
        },
        primary: {
          backgroundColor: theme.colors.primary,
        },
        secondary: {
          backgroundColor: theme.colors.secondary,
        },
        success: {
          backgroundColor: theme.colors.success,
        },
        warning: {
          backgroundColor: theme.colors.warning,
        },
      },
    },
  },
  indicator: {
    alignItems: 'center',
    borderColor: theme.colors.borderMuted,
    borderRadius: theme.borderRadius.full,
    borderWidth: theme.size(0.5),
    justifyContent: 'center',
    variants: {
      checked: {
        true: {},
      },
      disabled: {
        true: {},
      },
      size: {
        lg: {
          height: theme.size(7),
          width: theme.size(7),
        },
        md: {
          height: theme.size(6),
          width: theme.size(6),
        },
        sm: {
          height: theme.size(4),
          width: theme.size(4),
        },
      },
      variant: {
        error: {},
        info: {},
        primary: {},
        secondary: {},
        success: {},
        warning: {},
      },
    },
    compoundVariants: [
      {
        checked: true,
        variant: 'error',
        styles: {
          borderColor: theme.colors.error,
        },
      },
      {
        checked: true,
        variant: 'info',
        styles: {
          borderColor: theme.colors.info,
        },
      },
      {
        checked: true,
        variant: 'primary',
        styles: {
          borderColor: theme.colors.primary,
        },
      },
      {
        checked: true,
        variant: 'secondary',
        styles: {
          borderColor: theme.colors.secondary,
        },
      },
      {
        checked: true,
        variant: 'success',
        styles: {
          borderColor: theme.colors.success,
        },
      },
      {
        checked: true,
        variant: 'warning',
        styles: {
          borderColor: theme.colors.warning,
        },
      },
    ],
  },
  root: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.space('sm'),
    minHeight: theme.size(10),
    variants: {
      checked: {
        true: {},
      },
      disabled: {
        true: {
          opacity: 0.5,
        },
      },
      size: {
        lg: {},
        md: {},
        sm: {},
      },
      variant: {
        error: {},
        info: {},
        primary: {},
        secondary: {},
        success: {},
        warning: {},
      },
    },
  },
}));
